import { h, clear } from './dom.js'
import { on } from '../lib/bus.js'
import { state, patchOverlay, setFps, setPrefs, replaceStamps, applyScaffold, hasStamps } from '../lib/state.js'
import { distributeStamps } from '../lib/distribute.js'
import { BOARD_THEMES, PIECE_SETS, FPS_PRESETS, DEFAULT_OVERLAY } from '../config.js'
import { formatTime, formatFileSize } from '../lib/format.js'
import { toast } from './toast.js'
import { confirmDialog } from './dialog.js'

export function mountSettingsPanel(parent) {
  // --- Video card ----------------------------------------------------------
  const videoInfo = h('div.hint', {}, 'No video loaded.')
  const fpsSelect = h(
    'select.text-input',
    {
      'aria-label': 'Frame rate used for frame stepping',
      onChange: () => setFps(parseFloat(fpsSelect.value)),
    },
    FPS_PRESETS.map((fps) =>
      h('option', { value: String(fps), selected: fps === state.video.fps ? '' : null }, `${fps} fps`),
    ),
  )
  const autoFillCheck = checkbox('Pre-fill timestamps when video loads', state.prefs.autoFill, (c) =>
    setPrefs({ autoFill: c }),
  )
  const curveSelect = h(
    'select.text-input',
    { 'aria-label': 'Timestamp distribution curve', onChange: () => setPrefs({ curve: curveSelect.value }) },
    h('option', { value: 'even' }, 'Evenly spaced'),
    h('option', { value: 'paced' }, 'Typical game pacing — openings quick, middlegame slow'),
  )
  const distributeBtn = h('button.btn', { type: 'button', onClick: distributeNow }, 'Distribute now')
  const clearAllBtn = h('button.btn.ghost', { type: 'button', onClick: clearAllStamps }, 'Clear all tags')

  async function distributeNow() {
    const stamps = distributeStamps({
      count: state.pgn.moves.length,
      duration: state.video.duration,
      curve: state.prefs.curve,
    })
    if (!stamps.length) {
      toast('Load a game and a video first — distribution needs both', { kind: 'warn' })
      return
    }
    if (hasStamps()) {
      const ok = await confirmDialog({
        title: 'Replace timestamps?',
        message: 'This replaces every existing timestamp with the distribution scaffold.',
        confirmLabel: 'Distribute',
        danger: true,
      })
      if (!ok) return
    }
    applyScaffold(stamps)
    toast(`Spread ${stamps.length} moves across the video — refine each as you watch`)
  }

  async function clearAllStamps() {
    if (!hasStamps()) return
    const ok = await confirmDialog({
      title: 'Clear all timestamps?',
      message: 'Every tagged move goes back to untagged.',
      confirmLabel: 'Clear all',
      danger: true,
    })
    if (ok) replaceStamps([])
  }

  const videoCard = h(
    'section.card',
    {},
    h('h2.card-title', {}, 'Video'),
    videoInfo,
    h('label.field', {}, h('span.field-label', {}, 'Frame rate (for , and . stepping)'), fpsSelect),
    autoFillCheck.row,
    h('label.field', {}, h('span.field-label', {}, 'Distribution curve'), curveSelect),
    h('div.btn-row', {}, distributeBtn, clearAllBtn),
    h(
      'p.hint',
      {},
      'Pre-filled timestamps are a scaffold, not truth — they only appear on a blank sheet, and re-tagging a move simply overwrites its guess.',
    ),
  )

  function renderPrefs() {
    autoFillCheck.input.checked = state.prefs.autoFill
    if (curveSelect.value !== state.prefs.curve) curveSelect.value = state.prefs.curve
  }

  function renderVideoInfo() {
    clear(videoInfo)
    const v = state.video
    if (!v.fileName) {
      videoInfo.append('No video loaded.')
      return
    }
    videoInfo.append(
      h('div', { class: 'mono', style: { overflowWrap: 'anywhere' } }, v.fileName),
      h(
        'div',
        {},
        [
          v.width && v.height ? `${v.width}×${v.height}` : null,
          v.duration ? formatTime(v.duration) : null,
          v.size ? formatFileSize(v.size) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      ),
    )
    if (String(fpsSelect.value) !== String(v.fps)) fpsSelect.value = String(v.fps)
  }

  // --- Overlay card --------------------------------------------------------
  const visibleCheck = checkbox('Show board overlay', state.overlay.visible, (checked) =>
    patchOverlay({ visible: checked }),
  )
  const showBeforeCheck = checkbox(
    'Show starting position before the first tag',
    state.overlay.showBefore,
    (checked) => patchOverlay({ showBefore: checked }),
  )
  const coordsCheck = checkbox('Show coordinates', state.overlay.coords, (checked) =>
    patchOverlay({ coords: checked }),
  )

  const swatches = h(
    'div.swatch-row',
    {},
    Object.entries(BOARD_THEMES).map(([key, theme]) =>
      h('button.swatch', {
        type: 'button',
        title: theme.label,
        dataset: { theme: key },
        style: { '--sw-light': theme.light, '--sw-dark': theme.dark },
        onClick: () => patchOverlay({ theme: key }),
      }),
    ),
  )

  const piecesSelect = h(
    'select.text-input',
    { 'aria-label': 'Piece set', onChange: () => patchOverlay({ pieces: piecesSelect.value }) },
    Object.entries(PIECE_SETS).map(([key, set]) =>
      h('option', { value: key, selected: key === state.overlay.pieces ? '' : null }, set.label),
    ),
  )

  const whiteBtn = h('button.btn', { type: 'button', onClick: () => patchOverlay({ orientation: 'white' }) }, 'White ⬓')
  const blackBtn = h('button.btn', { type: 'button', onClick: () => patchOverlay({ orientation: 'black' }) }, 'Black ⬔')

  const modeOverlayBtn = h(
    'button.btn',
    { type: 'button', title: 'Board floats over the footage', onClick: () => patchOverlay({ mode: 'overlay' }) },
    'Over video',
  )
  const modeSplitBtn = h(
    'button.btn',
    {
      type: 'button',
      title: 'Classic Shorts canvas: board panel on top, footage below (exports 1080×1920)',
      onClick: () => patchOverlay({ mode: 'split' }),
    },
    'Split — board top',
  )

  const shareValue = h('span.hint.mono', {}, '')
  const shareRange = h('input', {
    type: 'range',
    min: '0.4',
    max: '0.65',
    step: '0.0025',
    value: String(state.overlay.split.share),
    'aria-label': 'Board panel height as a share of the frame',
    onInput: () =>
      patchOverlay({ split: { ...state.overlay.split, share: Number.parseFloat(shareRange.value) } }),
  })
  const shareField = h(
    'div.field',
    {},
    h('div.field-row', {}, h('span.field-label', {}, 'Board panel height'), h('span.transport-spacer'), shareValue),
    shareRange,
  )

  const posValue = h('span.hint.mono', {}, '')
  const posRange = h('input', {
    type: 'range',
    min: '0',
    max: '1',
    step: '0.01',
    value: String(state.overlay.split.pos),
    'aria-label': 'Which part of the footage shows in the bottom band',
    onInput: () =>
      patchOverlay({ split: { ...state.overlay.split, pos: Number.parseFloat(posRange.value) } }),
  })
  const posField = h(
    'div.field',
    {},
    h('div.field-row', {}, h('span.field-label', {}, 'Video framing'), h('span.transport-spacer'), posValue),
    posRange,
  )

  const opacityValue = h('span.hint.mono', {}, '100%')
  const opacityRange = h('input', {
    type: 'range',
    min: '0.3',
    max: '1',
    step: '0.05',
    value: String(state.overlay.opacity),
    'aria-label': 'Overlay opacity',
    onInput: () => patchOverlay({ opacity: parseFloat(opacityRange.value) }),
  })

  const sizeValue = h('span.hint.mono', {}, '')
  const sizeRange = h('input', {
    type: 'range',
    min: '0.15',
    max: '0.95',
    step: '0.01',
    value: String(state.overlay.size),
    'aria-label': 'Board size relative to video height',
    onInput: () => patchOverlay({ size: parseFloat(sizeRange.value) }),
  })

  const sizeField = h(
    'div.field',
    {},
    h('div.field-row', {}, h('span.field-label', {}, 'Size'), h('span.transport-spacer'), sizeValue),
    sizeRange,
  )
  const resetBtn = h(
    'button.btn.ghost',
    {
      type: 'button',
      onClick: () =>
        patchOverlay({
          customized: false,
          x: DEFAULT_OVERLAY.x,
          y: DEFAULT_OVERLAY.y,
          size: DEFAULT_OVERLAY.size,
        }),
    },
    'Reset placement',
  )
  const modeHint = h('p.hint')

  const overlayCard = h(
    'section.card',
    {},
    h('h2.card-title', {}, 'Board overlay'),
    visibleCheck.row,
    showBeforeCheck.row,
    h('div.field', {}, h('span.field-label', {}, 'Layout'), h('div.btn-row', {}, modeOverlayBtn, modeSplitBtn)),
    h('div.field', {}, h('span.field-label', {}, 'Board colors'), swatches),
    h('label.field', {}, h('span.field-label', {}, 'Piece set'), piecesSelect),
    h('div.field', {}, h('span.field-label', {}, 'Point of view'), h('div.btn-row', {}, whiteBtn, blackBtn)),
    coordsCheck.row,
    sizeField,
    shareField,
    posField,
    h(
      'div.field',
      {},
      h('div.field-row', {}, h('span.field-label', {}, 'Opacity'), h('span.transport-spacer'), opacityValue),
      opacityRange,
    ),
    resetBtn,
    modeHint,
  )

  function renderOverlay() {
    const o = state.overlay
    const split = o.mode === 'split'
    visibleCheck.input.checked = o.visible
    showBeforeCheck.input.checked = o.showBefore
    coordsCheck.input.checked = o.coords
    modeOverlayBtn.classList.toggle('primary', !split)
    modeSplitBtn.classList.toggle('primary', split)
    sizeField.hidden = split
    resetBtn.hidden = split
    shareField.hidden = !split
    posField.hidden = !split
    shareValue.textContent = `${Math.round(o.split.share * 100)}% of frame`
    if (Math.abs(Number.parseFloat(shareRange.value) - o.split.share) > 0.002) {
      shareRange.value = String(o.split.share)
    }
    posValue.textContent = `${Math.round(o.split.pos * 100)}%`
    if (Math.abs(Number.parseFloat(posRange.value) - o.split.pos) > 0.005) {
      posRange.value = String(o.split.pos)
    }
    modeHint.textContent = split
      ? 'Classic Shorts canvas: board panel on top, footage cropped into the band below. Exports at 1080×1920.'
      : 'Drag the board on the video to reposition it; use its corner handle to resize.'
    swatches.querySelectorAll('.swatch').forEach((el) => {
      el.classList.toggle('active', el.dataset.theme === o.theme)
    })
    if (piecesSelect.value !== o.pieces) piecesSelect.value = o.pieces
    whiteBtn.classList.toggle('primary', o.orientation !== 'black')
    blackBtn.classList.toggle('primary', o.orientation === 'black')
    opacityValue.textContent = `${Math.round(o.opacity * 100)}%`
    if (Math.abs(parseFloat(opacityRange.value) - o.opacity) > 0.001) {
      opacityRange.value = String(o.opacity)
    }
    sizeValue.textContent = `${Math.round(o.size * 100)}% of height`
    if (Math.abs(parseFloat(sizeRange.value) - o.size) > 0.005) {
      sizeRange.value = String(o.size)
    }
  }

  on('video', renderVideoInfo)
  on('overlay', renderOverlay)
  on('prefs', renderPrefs)
  renderVideoInfo()
  renderOverlay()
  renderPrefs()

  parent.append(videoCard, overlayCard)
}

function checkbox(label, checked, onChange) {
  const input = h('input', {
    type: 'checkbox',
    checked: checked ? '' : null,
    onChange: () => onChange(input.checked),
  })
  const row = h('label.radio-row', {}, input, label)
  return { row, input }
}
